import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Undo2,
  Clock,
  Package,
  AlertCircle,
  Loader2,
  CheckCircle2,
  History as HistoryIcon,
  ArrowLeft,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const History = ({ token, tipoTrabajo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [undoing, setUndoing] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const loadTriggerRef = useRef(0);
  
  // Redirigir si no hay token
  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  const loadHistory = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    let timeoutId = null;
    try {
      // Timeout de 15 segundos para evitar que se quede colgado
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const response = await axios.get(`${API}/history`, {
        params: { 
          fecha: selectedDate,
          tipo_articulo: tipoTrabajo // Agregar filtro por tipo de artículo
        },
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        timeout: 15000,  // Timeout adicional de axios
      });
      
      if (timeoutId) clearTimeout(timeoutId);
      setHistorial(response.data.historial || []);
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      console.error("Error loading history:", error);
      
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Timeout al cargar historial. Intenta de nuevo.");
      } else if (error.response) {
        toast.error(`Error al cargar historial: ${error.response.data?.detail || error.message}`);
      } else {
        toast.error("Error al cargar historial. Verifica la conexión.");
      }
      
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, tipoTrabajo, token]);

  // Cargar historial cuando se navega a la página o cambian los filtros
  // Este efecto se ejecuta cuando se monta el componente o cambian las dependencias
  useEffect(() => {
    // Solo ejecutar si estamos en la ruta de historial
    if (location.pathname !== '/history') {
      return;
    }

    if (!token) {
      setLoading(false);
      return;
    }

    // Incrementar trigger para forzar recarga
    loadTriggerRef.current += 1;
    const currentTrigger = loadTriggerRef.current;

    // Forzar carga inmediata - resetear estado y cargar
    setHistorial([]);
    setLoading(true);

    // Función para cargar datos - ejecutar inmediatamente
    const loadData = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        
        const response = await axios.get(`${API}/history`, {
          params: { 
            fecha: selectedDate,
            tipo_articulo: tipoTrabajo
          },
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
          timeout: 15000,
        });
        
        // Solo actualizar si este es el trigger más reciente
        if (loadTriggerRef.current === currentTrigger) {
          clearTimeout(timeoutId);
          // Ordenar historial por fecha/hora descendente (más reciente primero)
          const historialOrdenado = (response.data.historial || []).sort((a, b) => {
            // Comparar por fecha primero
            if (a.FechaProceso && b.FechaProceso) {
              const fechaA = new Date(a.FechaProceso);
              const fechaB = new Date(b.FechaProceso);
              if (fechaA.getTime() !== fechaB.getTime()) {
                return fechaB.getTime() - fechaA.getTime(); // DESC
              }
            }
            // Si las fechas son iguales, comparar por hora
            const horaA = a.HoraProceso || 0;
            const horaB = b.HoraProceso || 0;
            return horaB - horaA; // DESC
          });
          setHistorial(historialOrdenado);
          setLoading(false);
        }
      } catch (error) {
        // Solo actualizar si este es el trigger más reciente
        if (loadTriggerRef.current === currentTrigger) {
          console.error('Error loading history:', error);
          
          if (error.name === 'AbortError' || error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            toast.error("Timeout al cargar historial. Intenta de nuevo.");
          } else if (error.response) {
            toast.error(`Error al cargar historial: ${error.response.data?.detail || error.message}`);
          } else {
            toast.error("Error al cargar historial. Verifica la conexión.");
          }
          
          setHistorial([]);
          setLoading(false);
        }
      }
    };

    // Ejecutar inmediatamente sin delays
    loadData();
  }, [location.pathname, token, selectedDate, tipoTrabajo]); // Cuando cambia la ruta, token, fecha o tipo

  const handleUndo = async (item) => {
    if (!window.confirm(
      `¿Deshacer proceso ${item.TipoProceso}?\n\n` +
      `Artículo: ${item.CodigoArticulo}\n` +
      `${item.DescripcionArticulo}\n` +
      `Unidades: ${item.UnidPendProceso}`
    )) {
      return;
    }

    setUndoing(item.IdCBParte);
    try {
      const response = await axios.post(
        `${API}/undo-process`,
        {
          IdCBParte: item.IdCBParte,
          CodigoBarrasParte: item.CodigoBarrasParte,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        // Recargar historial
        await loadHistory();
      }
    } catch (error) {
      console.error("Error undoing:", error);
      const errorMsg = error.response?.data?.detail || "Error al deshacer operación";
      toast.error(errorMsg);
    } finally {
      setUndoing(null);
    }
  };

  const formatFecha = (fechaStr, horaDecimal) => {
    if (!fechaStr) return "";
    try {
      const fecha = new Date(fechaStr);
      
      // Si tenemos hora decimal (0.0 a 1.0), convertirla a horas, minutos y segundos
      if (horaDecimal !== undefined && horaDecimal !== null && horaDecimal > 0) {
        // horaDecimal es una fracción del día (0.0 = medianoche, 0.5 = mediodía, 1.0 = medianoche siguiente)
        const horasTotales = horaDecimal * 24;
        const horas = Math.floor(horasTotales);
        const minutosTotales = (horasTotales - horas) * 60;
        const minutos = Math.floor(minutosTotales);
        const segundos = Math.floor((minutosTotales - minutos) * 60);
        
        // Establecer la hora en la fecha
        fecha.setHours(horas, minutos, segundos, 0);
      }
      
      return fecha.toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return fechaStr;
    }
  };

  // SIEMPRE renderizar algo - incluso durante la carga
  // Esto asegura que el usuario vea algo inmediatamente al navegar
  // En móvil, es crítico que esto se renderice inmediatamente
  if (loading) {
    return (
      <div 
        className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center" 
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          zIndex: 9999, 
          width: '100%', 
          height: '100%',
          overflow: 'auto'
        }}
      >
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-700 font-semibold text-lg mb-2">Cargando historial...</p>
            <p className="text-sm text-gray-500 text-center mb-6">
              Por favor espera, esto puede tardar unos segundos
            </p>
            <Button
              onClick={() => {
                setLoading(false);
                navigate('/');
              }}
              variant="outline"
              size="default"
              className="min-w-[150px]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Cancelar y volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Card className="mb-3 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg p-3 sm:p-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 sm:gap-3">
                <HistoryIcon className="h-6 w-6 sm:h-8 sm:w-8" />
                <div>
                  <CardTitle className="text-lg sm:text-2xl font-bold">
                    Historial
                  </CardTitle>
                  <Badge 
                    className={`mt-1 ${
                      tipoTrabajo === 'molduras' 
                        ? 'bg-blue-500' 
                        : 'bg-green-500'
                    } text-white text-xs`}
                  >
                    {tipoTrabajo === 'molduras' ? 'Molduras' : 'Puertas'}
                  </Badge>
                </div>
              </div>
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 text-xs sm:text-sm"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                Volver
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Filtro de fecha */}
        <Card className="mb-3 shadow-md">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Fecha:
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                className="flex-1 min-w-[150px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Button
                onClick={() => setSelectedDate(new Date().toISOString().split("T")[0])}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                Hoy
              </Button>
            </div>
            {historial.length > 0 && (
              <p className="text-xs text-gray-600 mt-2">
                {historial.length} operacion{historial.length !== 1 ? "es" : ""} encontrada{historial.length !== 1 ? "s" : ""}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Lista de historial */}
        {historial.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-8 sm:py-12 text-center">
              <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-gray-400 mb-3 sm:mb-4" />
              <p className="text-lg sm:text-xl text-gray-600 mb-1 sm:mb-2">
                No hay operaciones recientes
              </p>
              <p className="text-sm sm:text-base text-gray-500">
                Las operaciones procesadas aparecerán aquí
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {historial.map((item) => (
              <Card
                key={item.IdCBParte}
                className="shadow-md hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-3 sm:p-4">
                  {/* Header con código y badges */}
                  <div className="flex items-start gap-2 mb-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className="font-mono text-xs bg-blue-50 border-blue-200 flex-shrink-0"
                    >
                      {item.CodigoBarrasParcial}
                    </Badge>
                    <Badge
                      className={
                        item.TipoProceso === "Moldura"
                          ? "bg-green-600 text-xs"
                          : "bg-purple-600 text-xs"
                      }
                    >
                      {item.TipoProceso}
                    </Badge>
                    <Badge
                      variant="secondary"
                      className={
                        item.TipoRegistro === "Inicial"
                          ? "bg-blue-100 text-blue-700 text-xs"
                          : "bg-orange-100 text-orange-700 text-xs"
                      }
                    >
                      {item.TipoRegistro}
                    </Badge>
                  </div>

                  {/* Información del artículo */}
                  <div className="mb-2">
                    <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                      {item.CodigoArticulo}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-700 line-clamp-2">
                      {item.DescripcionArticulo}
                      {item.Descripcion2Articulo && ` ${item.Descripcion2Articulo}`}
                    </p>
                  </div>

                  {/* Unidades, fecha y botón */}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Package className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span>{item.UnidPendProceso} uds</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="text-xs">{formatFecha(item.FechaProceso, item.HoraProceso)}</span>
                      </div>
                    </div>

                    {/* Botón de deshacer */}
                    <Button
                      onClick={() => handleUndo(item)}
                      disabled={undoing === item.IdCBParte}
                      variant="destructive"
                      size="sm"
                      className="text-xs h-8 flex-shrink-0"
                    >
                      {undoing === item.IdCBParte ? (
                        <>
                          <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" />
                          <span className="hidden sm:inline">Deshaciendo...</span>
                          <span className="sm:hidden">...</span>
                        </>
                      ) : (
                        <>
                          <Undo2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          Deshacer
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer con instrucciones */}
        {historial.length > 0 && (
          <Alert className="mt-3 sm:mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs sm:text-sm">
              Al deshacer, las unidades volverán a su estado anterior.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default History;
